import React, { useState, useEffect, useContext } from 'react'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import axios from 'axios'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCommentDollar, faSave } from '@fortawesome/free-solid-svg-icons'
import Swal from 'sweetalert2'
import AuthContext from '../../middlewares/AuthContext'
import { useParams } from 'react-router-dom'
import CustomLoader from '../layouts/CustomLoader'

const url = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_URL
  : process.env.REACT_APP_URL_DEV

const BRAND      = '#6eb446'
const BRAND_DARK = '#2d5918'

interface AuthCtx {
  getHeaders: () => Promise<Record<string, string>>
}

interface MarketingForm {
  name                  : string
  description           : string
  street_address        : string
  address_line_2        : string
  city                  : string
  zip_code              : string
  state                 : string
  country               : string
  invoice_street_address: string
  invoice_address_line_2: string
  invoice_city          : string
  invoice_zip_code      : string
  invoice_state         : string
  invoice_country       : string
  invoice_email         : string
  price                 : number
}

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.78rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
    {children}
  </label>
)

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p style={{ fontWeight: 800, color: '#1e293b', fontSize: '0.82rem', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '1rem' }}>
    {children}
  </p>
)

const EditMarketing: React.FC = () => {
  const { getHeaders } = useContext(AuthContext) as AuthCtx
  const { t }          = useTranslation()
  const { id }         = useParams<{ id: string }>()

  const [loaded, setLoaded]         = useState(false)
  const [loading, setLoading]       = useState(false)
  const [formValues, setFormValues] = useState<MarketingForm | null>(null)
  const [markName, setMarkName]     = useState('')

  const formSchema = Yup.object().shape({
    name                  : Yup.string().min(3).max(255).required(t('utilities.is_a_required_field')),
    description           : Yup.string().min(3).max(255).required(t('utilities.is_a_required_field')),
    street_address        : Yup.string().min(3).max(255).required(t('utilities.is_a_required_field')),
    address_line_2        : Yup.string().max(255).nullable(),
    zip_code              : Yup.string().matches(/^[0-9]+$/, 'Must be only digits').min(3).max(10).required(t('utilities.is_a_required_field')),
    state                 : Yup.string().min(3).max(255).required(t('utilities.is_a_required_field')),
    country               : Yup.string().min(3).max(255).required(t('utilities.is_a_required_field')),
    invoice_street_address: Yup.string().min(3).max(255).required(t('utilities.is_a_required_field')),
    invoice_address_line_2: Yup.string().max(255).nullable(),
    invoice_zip_code      : Yup.string().matches(/^[0-9]+$/, 'Must be only digits').min(3).max(10).required(t('utilities.is_a_required_field')),
    invoice_state         : Yup.string().min(3).max(255).required(t('utilities.is_a_required_field')),
    invoice_country       : Yup.string().min(3).max(255).required(t('utilities.is_a_required_field')),
    invoice_email         : Yup.string().email().required(t('utilities.is_a_required_field')),
    price                 : Yup.number().typeError('Need a number').min(0).max(99).required(t('utilities.is_a_required_field')),
  })

  useEffect(() => {
    const fetchMarketing = async () => {
      try {
        const headers = await getHeaders()
        const res     = await axios.get(`${url}marketings/edit/${id}`, { headers })
        const mark    = res.data.message
        setMarkName(mark.name)
        setFormValues({
          name                  : mark.name,
          description           : mark.description,
          street_address        : mark.street_address,
          address_line_2        : mark.address_line_2,
          city                  : mark.city,
          zip_code              : mark.zip_code,
          state                 : mark.state,
          country               : mark.country,
          invoice_street_address: mark.invoice_street_address,
          invoice_address_line_2: mark.invoice_address_line_2,
          invoice_city          : mark.invoice_city,
          invoice_zip_code      : mark.invoice_zip_code,
          invoice_state         : mark.invoice_state,
          invoice_country       : mark.invoice_country,
          invoice_email         : mark.invoice_email,
          price                 : mark.price,
        })
      } catch (err: any) {
        Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
      } finally {
        setLoaded(true)
      }
    }
    fetchMarketing()
  }, [])

  const handleSubmit = async (values: MarketingForm) => {
    setLoading(true)
    try {
      const headers = await getHeaders()
      const res     = await axios.put<{ response: string }>(
        `${url}marketings/update/${id}`,
        values,
        { headers }
      )
      Swal.fire({ icon: 'success', title: res.data.response })
    } catch (err: any) {
      Swal.fire({
        icon : 'error',
        title: err?.response?.data?.response || 'Error',
        text : err?.response?.data?.message,
      })
    } finally {
      setLoading(false)
    }
  }

  if (!loaded) return <CustomLoader />

  return (
    <div className="row justify-content-center">
      <div className="col-lg-11">

        {/* Header card */}
        <div style={{
          backgroundColor: '#fff',
          borderRadius   : 16,
          boxShadow      : '0 4px 20px rgba(0,0,0,0.07)',
          border         : '1px solid #f1f5f9',
          overflow       : 'hidden',
          marginBottom   : '1.5rem',
        }}>
          <div style={{ height: 4, background: `linear-gradient(90deg, ${BRAND_DARK}, ${BRAND})` }} />
          <div style={{ padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10, backgroundColor: `${BRAND}18`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <FontAwesomeIcon icon={faCommentDollar} style={{ color: BRAND, fontSize: '1.1rem' }} />
            </div>
            <div>
              <p style={{ margin: 0, fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {t('marketing.create.edition')}
              </p>
              <p style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: '#1e293b' }}>
                {markName}
              </p>
            </div>
          </div>
        </div>

        {formValues && (
          <Formik
            initialValues={formValues}
            validationSchema={formSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            <Form>
              <div className="row g-4">

                {/* Name / Description / Price */}
                <div className="col-12">
                  <div style={{
                    backgroundColor: '#fff',
                    borderRadius   : 16,
                    boxShadow      : '0 4px 20px rgba(0,0,0,0.07)',
                    border         : '1px solid #f1f5f9',
                    overflow       : 'hidden',
                  }}>
                    <div style={{ height: 3, backgroundColor: BRAND }} />
                    <div style={{ padding: '1.5rem' }}>
                      <div className="row g-3">
                        <div className="col-md-5">
                          <FieldLabel>{t('marketing.name')} *</FieldLabel>
                          <Field type="text" name="name" className="form-control form-control-sm" />
                          <ErrorMessage name="name" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                        </div>
                        <div className="col-md-5">
                          <FieldLabel>{t('marketing.description')} *</FieldLabel>
                          <Field as="textarea" name="description" className="form-control form-control-sm" style={{ height: 70 }} />
                          <ErrorMessage name="description" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                        </div>
                        <div className="col-md-2">
                          <FieldLabel>{t('marketing.price')} *</FieldLabel>
                          <Field type="text" name="price" className="form-control form-control-sm" />
                          <ErrorMessage name="price" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Physical Address */}
                <div className="col-md-6">
                  <div style={{
                    backgroundColor: '#fff',
                    borderRadius   : 16,
                    boxShadow      : '0 4px 20px rgba(0,0,0,0.07)',
                    border         : '1px solid #f1f5f9',
                    overflow       : 'hidden',
                    height         : '100%',
                  }}>
                    <div style={{ height: 3, backgroundColor: BRAND }} />
                    <div style={{ padding: '1.5rem' }}>
                      <SectionTitle>{t('marketing.address')}</SectionTitle>

                      <div className="mb-3">
                        <FieldLabel>{t('marketing.create.street_address')} *</FieldLabel>
                        <Field type="text" name="street_address" className="form-control form-control-sm" />
                        <ErrorMessage name="street_address" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                      </div>
                      <div className="mb-3">
                        <FieldLabel>{t('marketing.create.address_line')}</FieldLabel>
                        <Field type="text" name="address_line_2" className="form-control form-control-sm" />
                      </div>
                      <div className="mb-3">
                        <FieldLabel>{t('marketing.create.city')} *</FieldLabel>
                        <Field type="text" name="city" className="form-control form-control-sm" />
                      </div>
                      <div className="mb-3">
                        <FieldLabel>{t('marketing.create.zip')} *</FieldLabel>
                        <Field type="text" name="zip_code" className="form-control form-control-sm" />
                        <ErrorMessage name="zip_code" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                      </div>
                      <div className="mb-3">
                        <FieldLabel>{t('marketing.create.state')} *</FieldLabel>
                        <Field type="text" name="state" className="form-control form-control-sm" />
                        <ErrorMessage name="state" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                      </div>
                      <div className="mb-3">
                        <FieldLabel>{t('marketing.create.country')} *</FieldLabel>
                        <Field type="text" name="country" className="form-control form-control-sm" />
                        <ErrorMessage name="country" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Invoice Address */}
                <div className="col-md-6">
                  <div style={{
                    backgroundColor: '#fff',
                    borderRadius   : 16,
                    boxShadow      : '0 4px 20px rgba(0,0,0,0.07)',
                    border         : '1px solid #f1f5f9',
                    overflow       : 'hidden',
                    height         : '100%',
                  }}>
                    <div style={{ height: 3, backgroundColor: '#3b82f6' }} />
                    <div style={{ padding: '1.5rem' }}>
                      <SectionTitle>{t('marketing.create.street_address_inv')}</SectionTitle>

                      <div className="mb-3">
                        <FieldLabel>{t('marketing.create.street_address_inv')} *</FieldLabel>
                        <Field type="text" name="invoice_street_address" className="form-control form-control-sm" />
                        <ErrorMessage name="invoice_street_address" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                      </div>
                      <div className="mb-3">
                        <FieldLabel>{t('marketing.create.address_line_inv')}</FieldLabel>
                        <Field type="text" name="invoice_address_line_2" className="form-control form-control-sm" />
                      </div>
                      <div className="mb-3">
                        <FieldLabel>{t('marketing.create.city_inv')} *</FieldLabel>
                        <Field type="text" name="invoice_city" className="form-control form-control-sm" />
                      </div>
                      <div className="mb-3">
                        <FieldLabel>{t('marketing.create.zip_inv')} *</FieldLabel>
                        <Field type="text" name="invoice_zip_code" className="form-control form-control-sm" />
                        <ErrorMessage name="invoice_zip_code" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                      </div>
                      <div className="mb-3">
                        <FieldLabel>{t('marketing.create.state_inv')} *</FieldLabel>
                        <Field type="text" name="invoice_state" className="form-control form-control-sm" />
                        <ErrorMessage name="invoice_state" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                      </div>
                      <div className="mb-3">
                        <FieldLabel>{t('marketing.create.country_inv')} *</FieldLabel>
                        <Field type="text" name="invoice_country" className="form-control form-control-sm" />
                        <ErrorMessage name="invoice_country" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                      </div>
                      <div className="mb-3">
                        <FieldLabel>{t('marketing.create.email_inv')} *</FieldLabel>
                        <Field type="email" name="invoice_email" className="form-control form-control-sm" />
                        <ErrorMessage name="invoice_email" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Submit */}
                <div className="col-12">
                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      display        : 'inline-flex',
                      alignItems     : 'center',
                      justifyContent : 'center',
                      gap            : '0.5rem',
                      width          : '100%',
                      padding        : '0.55rem 1.5rem',
                      borderRadius   : 9999,
                      backgroundColor: loading ? '#94a3b8' : BRAND,
                      color          : '#fff',
                      fontWeight     : 700,
                      fontSize       : '0.875rem',
                      border         : 'none',
                      cursor         : loading ? 'not-allowed' : 'pointer',
                      boxShadow      : loading ? 'none' : `0 4px 14px ${BRAND}55`,
                      transition     : 'filter 0.15s, transform 0.15s',
                    }}
                    onMouseEnter={e => { if (!loading) { e.currentTarget.style.filter = 'brightness(1.09)'; e.currentTarget.style.transform = 'translateY(-1px)' }}}
                    onMouseLeave={e => { e.currentTarget.style.filter = 'brightness(1)'; e.currentTarget.style.transform = 'translateY(0)' }}
                  >
                    {loading
                      ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                      : <FontAwesomeIcon icon={faSave} />
                    }
                    <span>{t('marketing.create.update_marketing')}</span>
                  </button>
                </div>

              </div>
            </Form>
          </Formik>
        )}

      </div>
    </div>
  )
}

export default EditMarketing
